<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { parseChatStartParam, telegramAdapter } from "./telegram";
import { authenticateTelegram } from "./telegramAuth";
import { MockApi } from "./mockApi";
import {
  MarketplaceRequestError,
  newClientMessageId,
  ServerApi,
  type MarketplaceApi,
} from "./marketplaceApi";
import { isSandboxCatalogPreview } from "./sandboxPreview";
import AppIcon from "./components/AppIcon.vue";
import {
  categories,
  emptyDraft,
  formatDate,
  formatMoney,
  formatTime,
  normalizeClock,
  type SellerProfile,
  type SellerReview,
  type ModerationReview,
  type AdComment,
  type ChatInboxItem,
  type CityLocation,
  type Conversation,
  type Draft,
  type Listing,
  type RegionLocation,
  type ScreenId,
} from "./types";

const sandboxCatalogPreview = isSandboxCatalogPreview(
  { VITE_SANDBOX_CATALOG_PREVIEW: import.meta.env.VITE_SANDBOX_CATALOG_PREVIEW },
  window.location.search,
  !telegramAdapter.isTelegram,
  window.location.origin,
);
const api: MarketplaceApi = sandboxCatalogPreview || !telegramAdapter.isPreview ? new ServerApi() : new MockApi();
const screen = ref<ScreenId>("feed");
const history = ref<ScreenId[]>([]);
const listings = ref<Listing[]>([]);
const conversations = ref<Conversation[]>([]);
const inboxItems = ref<ChatInboxItem[]>([]);
const inboxUnread = ref(0);
const blockedConversationIds = ref<Set<string>>(new Set());
const reportSheetOpen = ref(false);
const reportTargetMessageId = ref<string | null>(null);
const selectedListingId = ref("ad-1");
const activeChatId = ref("chat-1");
const selectedCategory = ref("Всё");
const searchQuery = ref("");
const messageInput = ref("");
const loading = ref(true);
const error = ref("");
const toast = ref("");
const filterOpen = ref(false);
const moderatorActionOpen = ref(false);
const colorScheme = ref(telegramAdapter.colorScheme);
const themeLabel = computed(() => colorScheme.value === "dark" ? "Тёмная" : "Светлая");
const authFailed = ref(false);
const authLoading = ref(false);
const authBlocked = computed(() => authFailed.value && telegramAdapter.isTelegram && !telegramAdapter.isPreview);
let unsubscribeTheme: (() => void) | null = null;
let unsubscribeChat: (() => void) | null = null;
const online = ref(!api.offline);
const draft = ref<Draft>(api.getDraft());
const selectedFiles = ref<File[]>([]);
const chatPhotoFiles = ref<File[]>([]);
const chatPhotoPreviews = ref<Map<File, string>>(new Map());
const chatSending = ref(false);
type PendingChatSend = {
  chatId: string;
  text: string;
  files: File[];
  clientMessageId: string;
};
type StoredChatDraft = {
  text: string;
  files: File[];
  previews: Map<File, string>;
  pending: PendingChatSend | null;
};
const pendingChatSend = ref<PendingChatSend | null>(null);
const chatDrafts = ref<Map<string, StoredChatDraft>>(new Map());
const comments = ref<AdComment[]>([]);
const sellerProfile = ref<SellerProfile | null>(null);
const sellerReviews = ref<SellerReview[]>([]);
const sellerReviewsCursor = ref<string | null>(null);
const sellerReviewsHasMore = ref(false);
const sellerTab = ref<"ads" | "reviews">("ads");
const sellerRatingFilter = ref<number | null>(null);
const moderationQueue = ref<ModerationReview[]>([]);
const moderationLoading = ref(false);
const commentInput = ref("");
const ratingChoice = ref<number | null>(null);
const ratingText = ref("");
const reviewEditId = ref<string | null>(null);
const reviewEditText = ref("");
const reviewEditRating = ref(5);
const reviewEditOutcome = ref<"bought" | "no_agreement" | "declined">("bought");
const reviewReplyId = ref<string | null>(null);
const reviewReplyText = ref("");
const reviewReportId = ref<string | null>(null);
const reviewReportReason = ref("other");
const reviewReportDetails = ref("");
const filters = ref({ min: "", max: "", onlyNew: false });
type CatalogLocationSelection = {
  regionCode: string;
  cityId: string;
  cityName: string;
};
const geographyRegions = ref<RegionLocation[]>([]);
const draftCitySuggestions = ref<CityLocation[]>([]);
const catalogCitySuggestions = ref<CityLocation[]>([]);
const catalogCityQuery = ref("");
const catalogLocation = ref<CatalogLocationSelection>({ regionCode: "", cityId: "", cityName: "" });
let draftCityLookupVersion = 0;
let catalogCityLookupVersion = 0;
let listingRequestVersion = 0;

const pageTitles: Record<ScreenId, string> = {
  feed: "Лента",
  search: "Поиск",
  "ad-detail": "Объявление",
  product: "Модель",
  comments: "Обсуждение",
  seller: "Профиль продавца",
  favorites: "Избранное",
  profile: "Мой профиль",
  settings: "Настройки",
  notifications: "Уведомления",
  "my-ads": "Мои объявления",
  "create-1": "Новое объявление",
  "create-2": "Новое объявление",
  "create-3": "Новое объявление",
  chats: "Чаты",
  chat: "Диалог",
  promotions: "Продвижение",
  moderator: "Модерация",
};

const pageTitle = computed(() => pageTitles[screen.value]);
const currentListing = computed(() => listings.value.find((item) => item.id === selectedListingId.value) ?? listings.value[0]);
const activeChat = computed(() => conversations.value.find((item) => item.id === activeChatId.value));
const activeChatBlocked = computed(() => Boolean(activeChat.value && blockedConversationIds.value.has(activeChat.value.id)));
const filteredListings = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  return listings.value.filter((item) => {
    const matchesQuery = !query || `${item.title} ${item.category} ${item.city}`.toLowerCase().includes(query);
    const min = filters.value.min ? Number(filters.value.min) : 0;
    const max = filters.value.max ? Number(filters.value.max) : Number.MAX_SAFE_INTEGER;
    return matchesQuery && item.price >= min && item.price <= max && (!filters.value.onlyNew || item.condition === "Новое");
  });
});
const favoriteListings = computed(() => listings.value.filter((item) => item.favorite));
const draftStep = computed(() => Number(screen.value.replace("create-", "")) || 1);
const hasBack = computed(() => history.value.length > 0 && screen.value !== "feed");
const statusLabel = computed(() => (online.value ? "Сеть стабильна" : "Нет сети"));
const catalogLocationLabel = computed(() => {
  const cityName = catalogLocation.value.cityName;
  if (cityName) return cityName;
  const region = geographyRegions.value.find((item) => item.code === catalogLocation.value.regionCode);
  return region?.name ?? "Вся Россия";
});
const hasCatalogLocationFilter = computed(
  () => Boolean(catalogLocation.value.regionCode || catalogLocation.value.cityId),
);

function announce(message: string): void {
  toast.value = message;
  window.setTimeout(() => {
    if (toast.value === message) toast.value = "";
  }, 2600);
}

function compactCondition(value: string): string {
  return value.replace(" состояние", "").replace("Есть следы использования", "Следы использования");
}

function formatInboxTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("ru-RU", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(date);
}

const sandboxCatalogScreens = new Set<ScreenId>(["feed", "search", "ad-detail"]);

function go(target: ScreenId, remember = true): void {
  if (sandboxCatalogPreview && !sandboxCatalogScreens.has(target)) {
    announce("Sandbox-каталог доступен только для просмотра объявления");
    return;
  }
  if (target === screen.value) return;
  if (remember) history.value.push(screen.value);
  screen.value = target;
  telegramAdapter.hapticLight();
  window.scrollTo({ top: 0, behavior: "smooth" });
}

function revokeChatPhotoPreviews(previews: Map<File, string>): void {
  for (const previewUrl of previews.values()) URL.revokeObjectURL(previewUrl);
}

function clearChatPhotoFiles(): void {
  revokeChatPhotoPreviews(chatPhotoPreviews.value);
  chatPhotoPreviews.value = new Map();
  chatPhotoFiles.value = [];
}

function storeActiveChatDraft(): void {
  const chatId = activeChatId.value;
  if (!chatId) return;
  const pending = pendingChatSend.value?.chatId === chatId ? pendingChatSend.value : null;
  if (!messageInput.value && !chatPhotoFiles.value.length && !pending) return;
  const nextDrafts = new Map(chatDrafts.value);
  const previous = nextDrafts.get(chatId);
  if (previous && previous.previews !== chatPhotoPreviews.value) {
    revokeChatPhotoPreviews(previous.previews);
  }
  nextDrafts.set(chatId, {
    text: messageInput.value,
    files: [...chatPhotoFiles.value],
    previews: chatPhotoPreviews.value,
    pending,
  });
  chatDrafts.value = nextDrafts;
  // Transfer ownership of the photo previews instead of revoking a saved draft.
  messageInput.value = "";
  chatPhotoFiles.value = [];
  chatPhotoPreviews.value = new Map();
  if (pending) pendingChatSend.value = null;
}

function restoreChatDraft(chatId: string): void {
  const nextDrafts = new Map(chatDrafts.value);
  const saved = nextDrafts.get(chatId);
  if (!saved) {
    messageInput.value = "";
    clearChatPhotoFiles();
    pendingChatSend.value = null;
    return;
  }
  nextDrafts.delete(chatId);
  chatDrafts.value = nextDrafts;
  messageInput.value = saved.text;
  chatPhotoFiles.value = saved.files;
  chatPhotoPreviews.value = saved.previews;
  pendingChatSend.value = saved.pending;
}

function settleStoredChatDraft(chatId: string, pending: PendingChatSend): void {
  const saved = chatDrafts.value.get(chatId);
  if (!saved || saved.pending !== pending) return;
  const nextDrafts = new Map(chatDrafts.value);
  if (saved.text.trim() === pending.text && sameChatFiles(saved.files, pending.files)) {
    revokeChatPhotoPreviews(saved.previews);
    nextDrafts.delete(chatId);
  } else {
    nextDrafts.set(chatId, { ...saved, pending: null });
  }
  chatDrafts.value = nextDrafts;
}

function clearStoredChatDrafts(): void {
  for (const saved of chatDrafts.value.values()) revokeChatPhotoPreviews(saved.previews);
  chatDrafts.value = new Map();
}

function disposeActiveChat(): void {
  unsubscribeChat?.();
  unsubscribeChat = null;
  if (activeChatId.value) {
    storeActiveChatDraft();
    api.releaseConversationMedia(activeChatId.value);
  }
  reportSheetOpen.value = false;
  reportTargetMessageId.value = null;
}

function back(): void {
  if (screen.value === "chat") disposeActiveChat();
  const previous = history.value.pop();
  screen.value = previous ?? "feed";
  telegramAdapter.hapticLight();
  window.scrollTo({ top: 0, behavior: "smooth" });
}

function navigateFromNav(target: ScreenId): void {
  if (sandboxCatalogPreview && target !== "feed") {
    announce("Sandbox-каталог доступен только для просмотра объявлений");
    return;
  }
  if (screen.value === "chat" && target !== "chat") disposeActiveChat();
  history.value = [];
  go(target, false);
}

function openListing(id: string): void {
  selectedListingId.value = id;
  go("ad-detail");
}

function openProduct(listing: Listing | undefined = currentListing.value): void {
  if (sandboxCatalogPreview) {
    announce("Sandbox-каталог доступен только для просмотра объявления");
    return;
  }
  if (listing) selectedListingId.value = listing.id;
  go("product");
}

async function openSeller(listing: Listing | undefined = currentListing.value): Promise<void> {
  if (sandboxCatalogPreview) {
    announce("Sandbox-каталог доступен только для просмотра объявления");
    return;
  }
  if (listing) selectedListingId.value = listing.id;
  sellerTab.value = "ads";
  sellerRatingFilter.value = null;
  const sellerId = listing?.sellerId;
  if (sellerId) {
    try {
      sellerProfile.value = await api.getSellerProfile(sellerId);
      const reviewPage = await api.getSellerReviews(sellerId);
      sellerReviews.value = reviewPage.items;
      sellerReviewsCursor.value = reviewPage.nextCursor;
      sellerReviewsHasMore.value = reviewPage.hasMore;
    } catch {
      sellerProfile.value = null;
      sellerReviews.value = [];
      sellerReviewsCursor.value = null;
      sellerReviewsHasMore.value = false;
      announce("Профиль продавца пока недоступен");
    }
  } else {
    sellerProfile.value = null;
    sellerReviews.value = [];
    sellerReviewsCursor.value = null;
    sellerReviewsHasMore.value = false;
  }
  go("seller");
}

async function loadSellerReviews(rating: number | null = sellerRatingFilter.value): Promise<void> {
  const sellerId = currentListing.value?.sellerId;
  if (!sellerId) return;
  sellerRatingFilter.value = rating;
  try {
    const page = await api.getSellerReviews(sellerId, rating ?? undefined);
    sellerReviews.value = page.items;
    sellerReviewsCursor.value = page.nextCursor;
    sellerReviewsHasMore.value = page.hasMore;
  } catch {
    announce("Отзывы пока недоступны");
  }
}

async function loadMoreSellerReviews(): Promise<void> {
  const sellerId = currentListing.value?.sellerId;
  if (!sellerId || !sellerReviewsHasMore.value || !sellerReviewsCursor.value) return;
  try {
    const page = await api.getSellerReviews(sellerId, sellerRatingFilter.value ?? undefined, sellerReviewsCursor.value);
    sellerReviews.value = [...sellerReviews.value, ...page.items];
    sellerReviewsCursor.value = page.nextCursor;
    sellerReviewsHasMore.value = page.hasMore;
  } catch {
    announce("Не удалось загрузить ещё отзывы");
  }
}

function beginReviewEdit(review: SellerReview): void {
  reviewEditId.value = review.id;
  reviewEditText.value = review.text;
  reviewEditRating.value = review.rating;
  reviewEditOutcome.value = review.outcome;
}

async function saveReviewEdit(review: SellerReview): Promise<void> {
  try {
    const manage = await api.getSellerReviewManage(review.id);
    await api.updateSellerReview(review.id, {
      version: manage.version,
      rating: reviewEditRating.value,
      text: reviewEditText.value.trim(),
      outcome: reviewEditOutcome.value,
    });
    reviewEditId.value = null;
    await loadSellerReviews();
    announce("Отзыв отправлен на повторную проверку");
  } catch {
    announce("Не удалось изменить отзыв: проверьте срок и актуальность версии");
  }
}

async function hideReview(review: SellerReview): Promise<void> {
  try {
    await api.hideSellerReview(review.id);
    sellerReviews.value = sellerReviews.value.filter((item) => item.id !== review.id);
    announce("Отзыв скрыт из вашего профиля");
  } catch {
    announce("Скрыть отзыв может только его автор");
  }
}

async function saveReviewReply(review: SellerReview): Promise<void> {
  const text = reviewReplyText.value.trim();
  if (!text) return;
  try {
    await api.replySellerReview(review.id, text);
    reviewReplyId.value = null;
    reviewReplyText.value = "";
    await loadSellerReviews();
    announce("Ответ сохранён");
  } catch {
    announce("Ответить может только продавец этого объявления");
  }
}

async function submitReviewReport(review: SellerReview): Promise<void> {
  try {
    await api.reportSellerReview(review.id, reviewReportReason.value, reviewReportDetails.value.trim());
    reviewReportId.value = null;
    reviewReportDetails.value = "";
    announce("Жалоба зарегистрирована и будет проверена");
  } catch {
    announce("Не удалось зарегистрировать жалобу");
  }
}

async function loadModerationQueue(): Promise<void> {
  moderationLoading.value = true;
  try {
    moderationQueue.value = await api.getModerationQueue();
  } catch {
    moderationQueue.value = [];
  } finally {
    moderationLoading.value = false;
  }
}

async function moderateReview(item: ModerationReview, action: "approve" | "reject" | "hide"): Promise<void> {
  try {
    await api.moderateSellerReview(item.reviewId, action);
    moderationQueue.value = moderationQueue.value.filter((entry) => entry.reviewId !== item.reviewId);
    announce(action === "approve" ? "Отзыв опубликован" : action === "reject" ? "Отзыв отклонён" : "Отзыв скрыт");
  } catch {
    announce("Решение доступно только модератору");
  }
}

async function openComments(): Promise<void> {
  if (sandboxCatalogPreview) {
    announce("Sandbox-каталог доступен только для просмотра объявления");
    return;
  }
  try {
    if (currentListing.value?.id) comments.value = await api.getAdComments(currentListing.value.id);
  } catch {
    comments.value = [];
    announce("Комментарии пока недоступны");
  }
  go("comments");
}

async function submitComment(): Promise<void> {
  const text = commentInput.value.trim();
  if (!currentListing.value?.id || !text) return;
  try {
    const comment = await api.addAdComment(currentListing.value.id, text);
    comments.value = [...comments.value, comment];
    commentInput.value = "";
    announce("Комментарий опубликован");
  } catch {
    announce("Не удалось опубликовать комментарий");
  }
}

async function loadGeography(): Promise<void> {
  try {
    geographyRegions.value = await api.getRegions();
  } catch {
    // A catalog lookup failure must not make the public feed unavailable.
    geographyRegions.value = [];
  }
}

async function loadDraftCitySuggestions(): Promise<void> {
  const requestVersion = ++draftCityLookupVersion;
  const regionCode = draft.value.regionCode;
  const query = draft.value.city.trim();
  if (!regionCode || query.length < 2) {
    draftCitySuggestions.value = [];
    return;
  }
  try {
    const cities = await api.searchCities(regionCode, query);
    if (requestVersion === draftCityLookupVersion && regionCode === draft.value.regionCode && query === draft.value.city.trim()) {
      draftCitySuggestions.value = cities;
    }
  } catch {
    if (requestVersion === draftCityLookupVersion) draftCitySuggestions.value = [];
  }
}

function onDraftRegionChange(): void {
  draftCityLookupVersion += 1;
  draftCitySuggestions.value = [];
  void updateDraft({ regionCode: draft.value.regionCode, city: "", cityId: "" });
}

function onDraftCityInput(): void {
  draftCitySuggestions.value = [];
  void updateDraft({ city: draft.value.city, cityId: "" });
  void loadDraftCitySuggestions();
}

function chooseDraftCity(city: CityLocation): void {
  draftCityLookupVersion += 1;
  draftCitySuggestions.value = [];
  void updateDraft({
    regionCode: city.region.code,
    city: city.name,
    cityId: city.id,
  });
}

function catalogLocationRequest(): { regionCode?: string; cityId?: string } {
  return {
    regionCode: catalogLocation.value.regionCode || undefined,
    cityId: catalogLocation.value.cityId || undefined,
  };
}

async function loadCatalogCitySuggestions(): Promise<void> {
  const requestVersion = ++catalogCityLookupVersion;
  const regionCode = catalogLocation.value.regionCode;
  const query = catalogCityQuery.value.trim();
  if (!regionCode || query.length < 2) {
    catalogCitySuggestions.value = [];
    return;
  }
  try {
    const cities = await api.searchCities(regionCode, query);
    if (
      requestVersion === catalogCityLookupVersion
      && regionCode === catalogLocation.value.regionCode
      && query === catalogCityQuery.value.trim()
    ) {
      catalogCitySuggestions.value = cities;
    }
  } catch {
    if (requestVersion === catalogCityLookupVersion) catalogCitySuggestions.value = [];
  }
}

function onCatalogRegionChange(): void {
  catalogCityLookupVersion += 1;
  catalogCityQuery.value = "";
  catalogCitySuggestions.value = [];
  catalogLocation.value = {
    regionCode: catalogLocation.value.regionCode,
    cityId: "",
    cityName: "",
  };
  void loadListings();
}

function onCatalogCityInput(): void {
  const hadCitySelection = Boolean(catalogLocation.value.cityId);
  catalogLocation.value = { ...catalogLocation.value, cityId: "", cityName: "" };
  if (hadCitySelection) void loadListings();
  void loadCatalogCitySuggestions();
}

function chooseCatalogCity(city: CityLocation): void {
  catalogCityLookupVersion += 1;
  catalogCityQuery.value = city.name;
  catalogCitySuggestions.value = [];
  catalogLocation.value = {
    regionCode: city.region.code,
    cityId: city.id,
    cityName: city.name,
  };
  void loadListings();
}

function clearCatalogLocation(reload = true): void {
  catalogCityLookupVersion += 1;
  catalogCityQuery.value = "";
  catalogCitySuggestions.value = [];
  catalogLocation.value = { regionCode: "", cityId: "", cityName: "" };
  if (reload) void loadListings();
}

async function loadListings(): Promise<void> {
  const requestVersion = ++listingRequestVersion;
  const category = selectedCategory.value;
  const location = catalogLocationRequest();
  loading.value = true;
  error.value = "";
  try {
    const nextListings = await api.getListings(category, location);
    if (requestVersion === listingRequestVersion) listings.value = nextListings;
  } catch {
    if (requestVersion === listingRequestVersion) error.value = "Лента временно недоступна";
  } finally {
    if (requestVersion === listingRequestVersion) loading.value = false;
  }
}

async function loadFavorites(): Promise<void> {
  try {
    const saved = await api.getFavorites();
    const savedIds = new Set(saved.map((item) => item.id));
    listings.value = listings.value.map((item) => ({ ...item, favorite: savedIds.has(item.id) }));
  } catch {
    error.value = "Не удалось обновить избранное";
  }
}

async function toggleFavorite(listing: Listing): Promise<void> {
  if (sandboxCatalogPreview) {
    announce("В sandbox-каталоге только просмотр: для действий откройте Mini App из Telegram");
    return;
  }
  try {
    listing.favorite = await api.toggleFavorite(listing.id);
    announce(listing.favorite ? "Сохранено в избранное" : "Удалено из избранного");
  } catch {
    announce("Нет сети — попробуйте ещё раз");
  }
}

function openCreate(): void {
  if (sandboxCatalogPreview) {
    announce("Создание объявлений подключается только после Telegram-входа");
    return;
  }
  go("create-1");
}

function selectCategory(category: string): void {
  selectedCategory.value = category;
  if (screen.value !== "search") go("search");
  void loadListings();
}

function applyFilters(): void {
  filterOpen.value = false;
  announce(filteredListings.value.length ? `Найдено: ${filteredListings.value.length}` : "Ничего не найдено");
}

function clearFilters(): void {
  filters.value = { min: "", max: "", onlyNew: false };
  filterOpen.value = false;
  clearCatalogLocation(false);
  void loadListings();
}

async function loadChats(): Promise<void> {
  if (sandboxCatalogPreview) {
    conversations.value = [];
    return;
  }
  try {
    const loaded = await api.getConversations();
    blockedConversationIds.value = new Set(
      loaded.filter((item) => item.blockedByMe).map((item) => item.id),
    );
    const currentMessages = new Map(conversations.value.map((item) => [item.id, item.messages]));
    conversations.value = loaded.map((item) => ({
      ...item,
      messages: currentMessages.get(item.id) ?? item.messages,
    }));
  } catch {
    error.value = "Чаты временно недоступны";
  }
}

async function openTelegramStartConversation(): Promise<void> {
  if (sandboxCatalogPreview || !telegramAdapter.isTelegram) return;
  const chatId = parseChatStartParam(telegramAdapter.startParam);
  if (!chatId || !conversations.value.some((conversation) => conversation.id === chatId)) return;
  await openChat(chatId);
}

async function loadInbox(): Promise<void> {
  if (sandboxCatalogPreview) {
    inboxItems.value = [];
    inboxUnread.value = 0;
    return;
  }
  try {
    const inbox = await api.getInbox();
    inboxItems.value = inbox.items;
    inboxUnread.value = inbox.unreadCount;
  } catch {
    error.value = "Уведомления временно недоступны";
  }
}

function appendChatMessage(chatId: string, message: Conversation["messages"][number]): void {
  conversations.value = conversations.value.map((conversation) => {
    if (conversation.id !== chatId || conversation.messages.some((item) => item.id === message.id)) {
      return conversation;
    }
    return {
      ...conversation,
      messages: [...conversation.messages, message],
      preview: message.text || "Фото",
      time: message.time,
      unread: message.author === "other" ? conversation.unread + 1 : conversation.unread,
    };
  });
  if (message.author === "other") {
    void loadInbox();
    if (activeChatId.value === chatId && typeof message.sequence === "number") {
      void api.markConversationRead(chatId, message.sequence).catch(() => undefined);
    }
  }
}

function applyConversationRead(chatId: string, lastReadSequence: number): void {
  conversations.value = conversations.value.map((conversation) => {
    if (conversation.id !== chatId) return conversation;
    return {
      ...conversation,
      messages: conversation.messages.map((message) =>
        message.author === "me" && (message.sequence ?? 0) <= lastReadSequence
          ? { ...message, status: "read" }
          : message,
      ),
    };
  });
}

function mergeConversationMessages(
  current: Conversation["messages"],
  incoming: Conversation["messages"],
): Conversation["messages"] {
  const byId = new Map([...current, ...incoming].map((message) => [message.id, message]));
  return [...byId.values()].sort((left, right) => (left.sequence ?? 0) - (right.sequence ?? 0));
}

async function loadChatMessages(chatId: string, afterSequence?: number): Promise<number> {
  const messages = await api.getConversationMessages(chatId, afterSequence);
  let latestSequence = afterSequence ?? 0;
  conversations.value = conversations.value.map((conversation) => {
    if (conversation.id !== chatId) return conversation;
    const mergedMessages =
      typeof afterSequence === "number"
        ? mergeConversationMessages(conversation.messages, messages)
        : messages;
    const latest = mergedMessages.at(-1)?.sequence;
    if (typeof latest === "number") latestSequence = Math.max(latestSequence, latest);
    return { ...conversation, messages: mergedMessages, unread: 0 };
  });
  if (latestSequence > 0) {
    void api.markConversationRead(chatId, latestSequence).catch(() => undefined);
  }
  await loadInbox();
  return latestSequence;
}

async function openChat(id: string): Promise<void> {
  if (sandboxCatalogPreview) {
    announce("Переписка доступна только после Telegram-входа");
    return;
  }
  try {
    disposeActiveChat();
    activeChatId.value = id;
    restoreChatDraft(id);
    const latestSequence = await loadChatMessages(id);
    unsubscribeChat = api.subscribeToConversation(
      id,
      (event) => {
        if (event.type === "message.created") {
          appendChatMessage(id, event.message);
        } else if (event.author === "other") {
          applyConversationRead(id, event.lastReadSequence);
        }
      },
      (afterSequence) => {
        void loadChatMessages(id, afterSequence).catch(() => undefined);
      },
      latestSequence,
    );
    go("chat");
  } catch {
    announce("Не удалось открыть диалог");
  }
}

async function startChatForCurrentListing(): Promise<void> {
  if (sandboxCatalogPreview || !currentListing.value) {
    announce("Переписка доступна только после Telegram-входа");
    return;
  }
  try {
    const conversation = await api.startConversation(currentListing.value.id);
    if (!conversations.value.some((item) => item.id === conversation.id)) {
      conversations.value = [conversation, ...conversations.value];
    }
    await openChat(conversation.id);
  } catch {
    announce("Не удалось начать диалог с продавцом");
  }
}

async function openSupportChat(): Promise<void> {
  if (sandboxCatalogPreview) {
    announce("Поддержка доступна после Telegram-входа");
    return;
  }
  try {
    if (!conversations.value.some((item) => item.isSupport)) await loadChats();
    const support = conversations.value.find((item) => item.isSupport);
    if (!support) throw new Error("support_unavailable");
    await openChat(support.id);
  } catch {
    announce("Не удалось открыть поддержку");
  }
}

async function toggleConversationBlock(): Promise<void> {
  const chat = activeChat.value;
  if (!chat || chat.isSupport) return;
  try {
    const blocked = activeChatBlocked.value
      ? await api.unblockConversation(chat.id)
      : await api.blockConversation(chat.id);
    const next = new Set(blockedConversationIds.value);
    if (blocked) next.add(chat.id);
    else next.delete(chat.id);
    blockedConversationIds.value = next;
    announce(blocked ? "Пользователь заблокирован: новые сообщения не отправятся" : "Пользователь разблокирован");
  } catch {
    announce("Не удалось изменить блокировку");
  }
}

async function markConversationSold(): Promise<void> {
  const chat = activeChat.value;
  if (!chat || chat.isSupport || !chat.canMarkSold) return;
  try {
    const updated = await api.markConversationSold(chat.id);
    conversations.value = conversations.value.map((item) =>
      item.id === updated.id ? { ...item, ...updated, messages: item.messages } : item,
    );
    announce("Объявление отмечено как проданное");
  } catch {
    announce("Не удалось закрыть сделку");
  }
}

async function submitChatRating(): Promise<void> {
  const chat = activeChat.value;
  const listing = currentListing.value;
  const listingId = chat?.listingId ?? listing?.id;
  if (!chat?.canRate || !listingId || !ratingChoice.value || ratingText.value.trim().length < 20) return;
  try {
    await api.rateAd(listingId, chat.id, ratingChoice.value, ratingText.value.trim());
    ratingChoice.value = null;
    ratingText.value = "";
    announce("Спасибо, оценка сохранена");
    await loadChats();
  } catch {
    announce("Не удалось сохранить оценку");
  }
}

function openMessageReport(messageId: string): void {
  reportTargetMessageId.value = messageId;
  reportSheetOpen.value = true;
}

async function submitMessageReport(
  reason: "spam" | "fraud" | "abuse" | "other",
): Promise<void> {
  const chat = activeChat.value;
  const messageId = reportTargetMessageId.value;
  if (!chat || !messageId) return;
  try {
    await api.reportMessage(chat.id, messageId, reason);
    reportSheetOpen.value = false;
    reportTargetMessageId.value = null;
    announce("Жалоба отправлена на проверку");
  } catch {
    announce("Не удалось отправить жалобу");
  }
}

async function requestTelegramWriteAccess(): Promise<void> {
  if (!conversations.value.length) {
    announce("Разрешение на уведомления можно запросить после первого диалога");
    return;
  }
  if (!telegramAdapter.isTelegram) {
    announce("Разрешение на уведомления запрашивается внутри Telegram");
    return;
  }
  const granted = await telegramAdapter.requestWriteAccess();
  if (!granted) {
    announce("Разрешение не выдано — уведомления останутся внутри приложения");
    return;
  }
  try {
    if (await api.setTelegramWriteAccess(true)) {
      telegramAdapter.hapticSuccess();
      announce("Уведомления в Telegram разрешены");
    }
  } catch {
    announce("Не удалось сохранить разрешение на уведомления");
  }
}

async function openInboxItem(item: ChatInboxItem): Promise<void> {
  if (!conversations.value.some((conversation) => conversation.id === item.conversationId)) {
    await loadChats();
  }
  await openChat(item.conversationId);
}

function hideBrokenListingImage(listing: Listing): void {
  listing.imageUrl = null;
}

function selectChatPhotos(event: Event): void {
  const input = event.target as HTMLInputElement;
  const selected = Array.from(input.files ?? []);
  input.value = "";
  const imageFiles = selected.filter((file) =>
    ["image/jpeg", "image/png", "image/webp"].includes(file.type),
  );
  if (!imageFiles.length && selected.length) {
    announce("Можно отправить только JPEG, PNG или WebP");
    return;
  }
  const available = 4 - chatPhotoFiles.value.length;
  const accepted = imageFiles.slice(0, Math.max(available, 0));
  const previews = new Map(chatPhotoPreviews.value);
  for (const file of accepted) {
    if (!previews.has(file)) previews.set(file, URL.createObjectURL(file));
  }
  chatPhotoPreviews.value = previews;
  chatPhotoFiles.value = [...chatPhotoFiles.value, ...accepted];
  if (accepted.length !== selected.length) announce("В одном сообщении — до 4 фото JPEG, PNG или WebP");
}

function removeChatPhoto(index: number): void {
  const file = chatPhotoFiles.value[index];
  if (!file) return;
  const previews = new Map(chatPhotoPreviews.value);
  const previewUrl = previews.get(file);
  if (previewUrl) URL.revokeObjectURL(previewUrl);
  previews.delete(file);
  chatPhotoPreviews.value = previews;
  chatPhotoFiles.value = chatPhotoFiles.value.filter((_, itemIndex) => itemIndex !== index);
}

function sameChatFiles(left: File[], right: File[]): boolean {
  return left.length === right.length && left.every((file, index) => file === right[index]);
}

function newChatClientMessageId(): string {
  return newClientMessageId();
}

function pendingSend(chatId: string, text: string, files: File[]) {
  const existing = pendingChatSend.value;
  if (
    existing
    && existing.chatId === chatId
    && existing.text === text
    && sameChatFiles(existing.files, files)
  ) {
    return existing;
  }
  const next = { chatId, text, files: [...files], clientMessageId: newChatClientMessageId() };
  pendingChatSend.value = next;
  return next;
}

function chatSendFailureText(cause: unknown): string {
  if (cause instanceof MarketplaceRequestError && cause.code === "rate_limited") {
    const retryAfter = cause.retryAfterSeconds ?? 1;
    return `Слишком много сообщений. Повторите через ${retryAfter} с`;
  }
  return "Не удалось отправить: сообщение и фото сохранены для повтора";
}

async function sendMessage(): Promise<void> {
  if (chatSending.value || !activeChat.value || activeChatBlocked.value) return;
  const text = messageInput.value.trim();
  const files = chatPhotoFiles.value;
  if (!text && !files.length) return;
  const pending = pendingSend(activeChat.value.id, text, files);
  chatSending.value = true;
  try {
    const sent = await api.sendMessage(
      pending.chatId,
      pending.text,
      pending.files,
      pending.clientMessageId,
    );
    appendChatMessage(pending.chatId, sent);
    if (activeChatId.value === pending.chatId) {
      const composerMatchesPending =
        messageInput.value.trim() === pending.text && sameChatFiles(chatPhotoFiles.value, pending.files);
      if (pendingChatSend.value === pending) pendingChatSend.value = null;
      if (composerMatchesPending) {
        messageInput.value = "";
        clearChatPhotoFiles();
      }
    } else {
      settleStoredChatDraft(pending.chatId, pending);
    }
    telegramAdapter.hapticSuccess();
    await nextTick();
    document.querySelector(".messages")?.scrollTo({ top: 99999, behavior: "smooth" });
  } catch (cause) {
    announce(chatSendFailureText(cause));
  } finally {
    chatSending.value = false;
  }
}

async function persistDraft(): Promise<void> {
  await api.saveDraft({ ...draft.value });
}

async function updateDraft(patch: Partial<Draft>): Promise<void> {
  draft.value = { ...draft.value, ...patch };
  try {
    await persistDraft();
  } catch {
    announce("Черновик сохранён на устройстве");
  }
}

function selectListingPhotos(event: Event): void {
  const input = event.target as HTMLInputElement;
  const files = Array.from(input.files ?? []);
  if (files.length > 12) {
    selectedFiles.value = files.slice(0, 12);
    announce("Можно добавить не более 12 фото");
    return;
  }
  selectedFiles.value = files;
}

async function continueCreate(target: "create-1" | "create-2" | "create-3"): Promise<void> {
  if (target === "create-3" && !draft.value.cityId) {
    announce("Выберите населённый пункт из справочника");
    return;
  }
  try {
    await persistDraft();
    go(target);
  } catch {
    announce("Нет сети — черновик останется на устройстве");
  }
}

function updateTime(value: string): void {
  const normalized = normalizeClock(value);
  if (normalized) {
    draft.value.time = normalized;
    void updateDraft({ time: normalized });
  } else if (value) {
    announce("Введите время в формате 24:00");
  }
}

async function submitDraft(): Promise<void> {
  const price = Number(draft.value.price);
  if (
    !draft.value.title.trim() ||
    !draft.value.category ||
    !draft.value.cityId ||
    !draft.value.condition ||
    !draft.value.description.trim() ||
    (draft.value.price && (!Number.isFinite(price) || price < 0)) ||
    (telegramAdapter.isTelegram && selectedFiles.value.length === 0)
  ) {
    announce(
      telegramAdapter.isTelegram && selectedFiles.value.length === 0
        ? "Добавьте хотя бы одно фото"
        : "Заполните обязательные поля объявления",
    );
    if (
      !draft.value.title.trim() ||
      !draft.value.category ||
      (draft.value.price && (!Number.isFinite(price) || price < 0))
    ) go("create-1");
    else if (!draft.value.cityId || !draft.value.condition || selectedFiles.value.length === 0) go("create-2");
    return;
  }
  try {
    await persistDraft();
    const published = await api.submitDraft(draft.value, selectedFiles.value);
    if (published) listings.value = [published, ...listings.value.filter((item) => item.id !== published.id)];
    draft.value = api.getDraft();
    selectedFiles.value = [];
    telegramAdapter.hapticSuccess();
    announce(published ? "Объявление опубликовано в sandbox" : "Объявление отправлено на проверку");
    go("feed");
  } catch {
    announce("Не удалось отправить объявление: черновик и фото сохранены для повтора");
  }
}

async function bootstrapAuth(): Promise<boolean> {
  if (!telegramAdapter.isTelegram || telegramAdapter.isPreview) return true;
  authLoading.value = true;
  authFailed.value = false;
  try {
    await authenticateTelegram(telegramAdapter.initData);
    return true;
  } catch {
    authFailed.value = true;
    return false;
  } finally {
    authLoading.value = false;
  }
}

async function retryAuth(): Promise<void> {
  if (!(await bootstrapAuth())) return;
  loading.value = true;
  await Promise.all([loadListings(), loadGeography(), loadChats(), loadInbox()]);
  await openTelegramStartConversation();
}

function toggleNetwork(): void {
  online.value = !online.value;
  api.setOffline(!online.value);
  announce(online.value ? "Соединение восстановлено" : "Включён режим без сети");
  if (online.value) void Promise.all([loadListings(), loadGeography(), loadChats(), loadInbox()]);
}

function shareCurrent(): void {
  if (sandboxCatalogPreview) {
    announce("Sandbox-объявления не публикуются по внешней ссылке");
    return;
  }
  telegramAdapter.share(`https://freevito.fidelio.pro/ad/${currentListing.value?.id ?? "preview"}`);
  announce("Ссылка готова для отправки");
}

function syncBackButton(): void {
  telegramAdapter.setBack(hasBack.value ? back : null);
}

watch(screen, syncBackButton, { immediate: true });
watch(screen, (next) => {
  if (next === "moderator") void loadModerationQueue();
});

onMounted(async () => {
  unsubscribeTheme = telegramAdapter.onThemeChange((scheme) => {
    colorScheme.value = scheme;
  });
  telegramAdapter.ready();
  const authenticated = await bootstrapAuth();
  if (authenticated) {
    await Promise.all([loadListings(), loadGeography(), loadChats(), loadInbox()]);
    await openTelegramStartConversation();
  } else loading.value = false;
});

onBeforeUnmount(() => {
  disposeActiveChat();
  clearStoredChatDrafts();
  unsubscribeTheme?.();
  telegramAdapter.setBack(null);
});
</script>

<template>
  <div class="app-shell" :class="{ 'chat-active': screen === 'chat' }" :data-screen="screen">
    <div v-if="telegramAdapter.isPreview" class="preview-ribbon" role="status">
      <template v-if="sandboxCatalogPreview">Sandbox · тестовый каталог · не для продажи · только просмотр</template>
      <template v-else>Предпросмотр · mock API</template>
      <button v-if="!sandboxCatalogPreview" type="button" @click="toggleNetwork">{{ statusLabel }}</button>
    </div>

    <header class="topbar" :class="{ 'topbar-back': hasBack }">
      <button v-if="hasBack" class="icon-button back-button" type="button" aria-label="Назад" @click="back">←</button>
      <div class="brand-lockup">
        <span class="brand-mark" aria-hidden="true">f</span>
        <div>
          <p class="eyebrow">NONAME · MARKETPLACE</p>
          <h1>{{ pageTitle }}</h1>
        </div>
      </div>
    </header>

    <main class="page-content" :aria-busy="loading">
      <div v-if="authBlocked" class="auth-blocked state-card" role="alert">
        <span class="success-mark" aria-hidden="true">!</span>
        <h2>Не удалось подтвердить Telegram-сессию</h2>
        <p>Откройте Mini App из Telegram и повторите вход. Данные не загружены.</p>
        <button class="primary-button" type="button" :disabled="authLoading" @click="void retryAuth()">{{ authLoading ? "Проверяем…" : "Повторить" }}</button>
      </div>
      <template v-else>
        <div v-if="!online" class="offline-bar" role="alert">
        <span class="status-dot status-dot-danger" aria-hidden="true"></span>
        <span>Нет сети. Изменения останутся в черновике.</span>
        <button type="button" @click="toggleNetwork">Повторить</button>
      </div>
      <div v-if="error" class="error-banner" role="alert">
        <span>{{ error }}</span><button type="button" @click="error = ''; void loadListings()">Повторить</button>
      </div>

      <!-- 1. Лента -->
      <section v-if="screen === 'feed'" class="screen screen-feed" aria-labelledby="feed-heading">
        <div class="feed-intro">
          <div>
            <p class="eyebrow">{{ sandboxCatalogPreview ? 'ТЕСТОВЫЙ КАТАЛОГ' : `КАТАЛОГ · ${catalogLocationLabel}` }}</p>
            <h2 id="feed-heading">Вещи с историей,<br /><em>люди рядом.</em></h2>
          </div>
          <button class="location-chip" type="button" aria-label="Изменить населённый пункт" @click="go('search')">{{ catalogLocationLabel }}</button>
        </div>
        <button class="search-launch" type="button" @click="go('search')">
          <span class="search-icon" aria-hidden="true">⌕</span><span>Найти среди объявлений</span><span class="search-kbd">⌘ K</span>
        </button>
        <div class="category-row" aria-label="Категории">
          <button v-for="category in categories" :key="category" class="category-chip" :class="{ selected: selectedCategory === category }" type="button" @click="selectCategory(category)">
            {{ category }}
          </button>
        </div>
        <div class="section-heading"><div><p class="eyebrow">ПОДБОРКА ДЛЯ ВАС</p><h3>Рядом с вами</h3></div><button class="text-button" type="button" @click="go('search')">Все →</button></div>
        <div v-if="loading" class="listing-grid" aria-label="Загрузка объявлений"><div v-for="i in 2" :key="i" class="listing-card skeleton-card"><div class="skeleton skeleton-visual"></div><div class="skeleton skeleton-line wide"></div><div class="skeleton skeleton-line"></div></div></div>
        <div v-else class="listing-grid">
          <article v-for="listing in listings" :key="listing.id" class="listing-card" @click="openListing(listing.id)">
            <div class="listing-visual" :class="[`tint-${listing.tint}`, { 'has-image': Boolean(listing.imageUrl) }]">
              <img v-if="listing.imageUrl" class="listing-image" :src="listing.imageUrl" :alt="`Фото: ${listing.title}`" loading="lazy" @error="hideBrokenListingImage(listing)" />
              <template v-else><span class="visual-symbol" aria-hidden="true">{{ listing.emoji }}</span><span class="visual-note">реальные фотографии · фото товара</span></template>
              <button v-if="!sandboxCatalogPreview" class="favorite-button" type="button" :aria-label="listing.favorite ? 'Убрать из избранного' : 'Добавить в избранное'" :aria-pressed="listing.favorite" @click.stop="toggleFavorite(listing)">{{ listing.favorite ? "♥" : "♡" }}</button>
            </div>
            <div class="listing-copy"><div class="listing-meta"><span>{{ listing.category }}</span><span>{{ listing.city }}</span></div><h4>{{ listing.title }}</h4><div class="listing-bottom"><strong>{{ formatMoney(listing.price) }}</strong><span class="condition-dot"><span class="status-dot" aria-hidden="true"></span>{{ compactCondition(listing.condition) }}</span></div></div>
          </article>
        </div>
        <div v-if="!sandboxCatalogPreview" class="quiet-card"><span class="quiet-icon" aria-hidden="true">✦</span><div><strong>Продать — бесплатно</strong><p>Разместите вещь за три шага. Без комиссии за публикацию.</p></div><button type="button" class="round-arrow" aria-label="Продать вещь" @click="openCreate">↗</button></div>
        <div v-else class="quiet-card catalog-readonly-card"><span class="quiet-icon" aria-hidden="true">◉</span><div><strong>Только просмотр</strong><p>Создание, избранное и чаты отключены в тестовом каталоге.</p></div></div>
      </section>

      <!-- 2. Поиск / категории / фильтры -->
      <section v-else-if="screen === 'search'" class="screen" aria-labelledby="search-heading">
        <div class="screen-heading"><p class="eyebrow">КАТАЛОГ</p><h2 id="search-heading">Найти своё</h2><p class="subhead">Населённый пункт, модель или просто настроение.</p></div>
        <label class="search-field"><span class="search-icon" aria-hidden="true">⌕</span><input v-model="searchQuery" type="search" placeholder="Например, лампа или камера" aria-label="Поиск объявлений" /><kbd>⌘ K</kbd></label>
        <div class="location-filter" aria-label="Фильтр по региону и населённому пункту">
          <label class="form-field">
            <span>Регион</span>
            <select v-model="catalogLocation.regionCode" @change="onCatalogRegionChange">
              <option value="">Вся Россия</option>
              <option v-for="region in geographyRegions" :key="region.code" :value="region.code">{{ region.name }}</option>
            </select>
          </label>
          <label v-if="catalogLocation.regionCode" class="form-field">
            <span>Населённый пункт</span>
            <input v-model="catalogCityQuery" type="search" inputmode="search" maxlength="100" placeholder="Начните вводить" aria-label="Населённый пункт для фильтра" @input="onCatalogCityInput" />
            <small>Не менее двух букв</small>
          </label>
          <div v-if="catalogCitySuggestions.length" class="city-suggestions" role="listbox" aria-label="Подходящие населённые пункты">
            <button v-for="city in catalogCitySuggestions" :key="city.id" type="button" role="option" @click="chooseCatalogCity(city)">{{ city.name }}</button>
          </div>
          <button v-if="hasCatalogLocationFilter" class="clear-filter location-reset" type="button" @click="() => clearCatalogLocation()">Сбросить место</button>
        </div>
        <div class="category-row category-row-wrap" aria-label="Фильтр по категориям"><button v-for="category in categories" :key="category" class="category-chip" :class="{ selected: selectedCategory === category }" type="button" @click="selectedCategory = category; void loadListings()">{{ category }}</button></div>
        <div class="result-toolbar"><div class="segmented" role="tablist"><button class="segment active" type="button" role="tab" aria-selected="true">Объявления</button><button class="segment" type="button" role="tab" aria-selected="false" @click="go('product')">Модели</button></div><button class="filter-button" type="button" @click="filterOpen = true">Фильтры <span v-if="filters.min || filters.max || filters.onlyNew || hasCatalogLocationFilter" class="filter-count">•</span></button></div>
        <div class="active-filter-row"><span class="result-count">{{ filteredListings.length }} результата</span><button v-if="filters.min || filters.max || filters.onlyNew || hasCatalogLocationFilter" class="clear-filter" type="button" @click="clearFilters">Сбросить</button></div>
        <div v-if="!filteredListings.length" class="empty-state"><div class="empty-illustration" aria-hidden="true">⌁</div><h3>Ничего не нашли</h3><p>Попробуйте убрать фильтр или изменить запрос.</p><button class="primary-button" type="button" @click="clearFilters">Показать всё</button></div>
        <div v-else class="listing-grid">
          <article v-for="listing in filteredListings" :key="listing.id" class="listing-card" @click="openListing(listing.id)">
            <div class="listing-visual" :class="[`tint-${listing.tint}`, { 'has-image': Boolean(listing.imageUrl) }]">
              <img v-if="listing.imageUrl" class="listing-image" :src="listing.imageUrl" :alt="`Фото: ${listing.title}`" loading="lazy" @error="hideBrokenListingImage(listing)" />
              <template v-else><span class="visual-symbol" aria-hidden="true">{{ listing.emoji }}</span><span class="visual-note">реальные фотографии · фото товара</span></template>
              <button v-if="!sandboxCatalogPreview" class="favorite-button" type="button" :aria-label="listing.favorite ? 'Убрать из избранного' : 'Добавить в избранное'" :aria-pressed="listing.favorite" @click.stop="toggleFavorite(listing)">{{ listing.favorite ? "♥" : "♡" }}</button>
            </div>
            <div class="listing-copy"><div class="listing-meta"><span>{{ listing.category }}</span><span>{{ listing.city }}</span></div><h4>{{ listing.title }}</h4><div class="listing-bottom"><strong>{{ formatMoney(listing.price) }}</strong><span>{{ listing.age }}</span></div></div>
          </article>
        </div>
      </section>

      <!-- 3. Карточка объявления -->
      <section v-else-if="screen === 'ad-detail' && currentListing" class="screen detail-screen" aria-labelledby="ad-heading">
        <div class="detail-hero listing-visual" :class="[`tint-${currentListing.tint}`, { 'has-image': Boolean(currentListing.imageUrl) }]">
          <img v-if="currentListing.imageUrl" class="listing-image detail-image" :src="currentListing.imageUrl" :alt="`Фото: ${currentListing.title}`" @error="hideBrokenListingImage(currentListing)" />
          <template v-else><span class="visual-symbol large" aria-hidden="true">{{ currentListing.emoji }}</span><span class="visual-note">здесь будет галерея товара</span></template>
          <span class="gallery-counter">{{ currentListing.imageUrl ? '1 / 1' : '0 / 0' }}</span>
        </div>
        <div v-if="!sandboxCatalogPreview" class="detail-actions"><button class="secondary-button" type="button" @click="toggleFavorite(currentListing)">{{ currentListing.favorite ? "♥ В избранном" : "♡ В избранное" }}</button><button class="secondary-button icon-text" type="button" @click="shareCurrent">↗ Поделиться</button></div>
        <p v-else class="catalog-readonly-note">Только просмотр: избранное и внешняя ссылка отключены.</p>
        <div class="eyebrow-row"><span class="soft-pill">{{ currentListing.category }}</span><span class="detail-age">{{ currentListing.age }}</span></div><h2 id="ad-heading" class="detail-title">{{ currentListing.title }}</h2><div class="detail-price">{{ formatMoney(currentListing.price) }}</div>
        <div class="facts-row"><span><b>Состояние</b>{{ currentListing.condition }}</span><span><b>Населённый пункт</b>{{ currentListing.city }}</span><span><b>Доставка</b>Самовывоз</span></div>
        <div class="body-copy"><p>{{ currentListing.description || 'Описание появится после публикации объявления.' }}</p></div>
        <div class="seller-card catalog-static" role="button" tabindex="0" @click="openSeller()" @keydown.enter="openSeller()"><span class="seller-avatar">{{ currentListing.seller[0] }}</span><span class="seller-info"><strong>{{ currentListing.seller }}</strong><span>Продавец объявления · рейтинг {{ currentListing.sellerRating === null ? "нет оценок" : `${currentListing.sellerRating.toFixed(1).replace('.', ',')} ★` }}</span></span><AppIcon name="chevron" :size="18" /></div>
        <div v-if="!sandboxCatalogPreview" class="sticky-cta"><button class="contact-fab" type="button" aria-label="Написать продавцу" title="Написать продавцу" @click="startChatForCurrentListing"><span aria-hidden="true">🗨️</span></button></div>
        <div v-else class="catalog-readonly-card detail-readonly-card"><span class="quiet-icon" aria-hidden="true">◉</span><div><strong>Связь с продавцом отключена</strong><p>Тестовый каталог не создаёт чаты и не хранит избранное.</p></div></div>
      </section>

      <!-- 4. Модель -->
      <section v-else-if="screen === 'product' && currentListing" class="screen" aria-labelledby="product-heading">
        <div class="product-header"><div class="product-glyph" aria-hidden="true">◈</div><div><p class="eyebrow">КАРТОЧКА МОДЕЛИ</p><h2 id="product-heading">{{ currentListing.product }}</h2><p class="subhead">Собираем знания пользователей, а не заменяем реальные объявления.</p></div></div>
        <div class="rating-panel"><div class="rating-number">{{ currentListing.productRating === null || currentListing.productRating === undefined ? "—" : currentListing.productRating.toFixed(1).replace('.', ',') }}</div><div><div class="stars" aria-label="Рейтинг товара">{{ currentListing.productRating ? "★★★★★" : "☆☆☆☆☆" }}</div><span>{{ currentListing.productReviewsCount ?? 0 }} оценок · один пользователь учитывается один раз</span></div><button class="icon-button" type="button" aria-label="Поставить свою оценку" @click="announce('Оценка доступна после закрытой сделки')">＋</button></div>
        <div class="section-heading compact"><div><p class="eyebrow">СВЯЗАННЫЕ ОБЪЯВЛЕНИЯ</p><h3>Сейчас продают</h3></div><button class="text-button" type="button" @click="go('search')">Все →</button></div>
        <article class="listing-card horizontal-card" @click="openListing(currentListing.id)"><div class="listing-visual compact-visual tint-lavender"><span class="visual-symbol" aria-hidden="true">{{ currentListing.emoji }}</span></div><div class="listing-copy"><div class="listing-meta"><span>{{ currentListing.city }}</span><span>{{ currentListing.condition }}</span></div><h4>{{ currentListing.title }}</h4><strong>{{ formatMoney(currentListing.price) }}</strong></div></article>
        <div class="action-pair"><button class="primary-button" type="button" @click="go('comments')">Обсудить модель</button><button class="secondary-button" type="button" @click="openCreate">Продать такую</button></div>
        <div class="info-note"><span class="quiet-icon" aria-hidden="true">i</span><p>Оценка модели и рейтинг продавца — разные вещи. Один пользователь учитывается один раз.</p></div>
      </section>

      <!-- 5. Комментарии -->
      <section v-else-if="screen === 'comments'" class="screen" aria-labelledby="comments-heading"><div class="screen-heading"><p class="eyebrow">{{ currentListing?.product ?? "ОБЪЯВЛЕНИЕ" }}</p><h2 id="comments-heading">Обсуждение</h2><p class="subhead">Вопросы и опыт владельцев, в одной ветке.</p></div><div class="comment-sort"><span>{{ comments.length }} сообщений</span><button class="text-button" type="button" @click="announce('Сначала новые')">Сначала новые⌄</button></div><div v-if="comments.length" class="comment-thread"><div v-for="comment in comments" :key="comment.id" class="comment-item"><span class="seller-avatar small">{{ comment.author[0] }}</span><div class="comment-main"><div class="comment-head"><strong>{{ comment.author }}</strong><span>{{ comment.time }}</span></div><p>{{ comment.text }}</p><button class="reply-button" type="button" @click="announce(`Ответ для ${comment.author}`)">Ответить</button></div></div></div><div v-else class="quiet-card"><span class="quiet-icon">?</span><p><strong>Пока нет комментариев</strong><br />Задайте первый вопрос продавцу и другим пользователям.</p></div><div class="composer"><input v-model="commentInput" type="text" placeholder="Написать комментарий" aria-label="Новый комментарий" @keyup.enter="submitComment" /><button class="send-button" type="button" aria-label="Отправить комментарий" :disabled="!commentInput.trim()" @click="submitComment">↑</button></div></section>

      <!-- 6. Продавец -->
      <section v-else-if="screen === 'seller' && currentListing" class="screen" aria-labelledby="seller-heading">
        <div class="seller-profile-head">
          <div class="seller-avatar profile-avatar">{{ (sellerProfile?.seller.displayName ?? currentListing.seller)[0] }}</div>
          <p class="eyebrow">ПРОФИЛЬ ПРОДАВЦА</p>
          <h2 id="seller-heading">{{ sellerProfile?.seller.displayName ?? currentListing.seller }}</h2>
          <p class="subhead">{{ sellerProfile?.seller.city ?? currentListing.city }} · на Noname с {{ sellerProfile?.seller.memberSince ?? "2024" }}</p>
          <div class="seller-rating">
            <strong>{{ sellerProfile?.reputation.average == null ? "—" : sellerProfile.reputation.average.toFixed(1).replace('.', ',') }}</strong>
            <span v-if="sellerProfile?.reputation.average != null">★★★★★</span><span v-else>Нет опубликованных оценок</span>
            <small>{{ sellerProfile?.reputation.reviewersCount ?? 0 }} учитываемых отзывов · новые отзывы проходят проверку</small>
          </div>
          <div v-if="sellerProfile" class="rating-distribution"><div v-for="star in [5, 4, 3, 2, 1]" :key="star" class="rating-row"><span>{{ star }} ★</span><span class="rating-track"><i :style="{ width: `${Math.min(100, ((sellerProfile.reputation.distribution[String(star)] ?? 0) / Math.max(1, sellerProfile.reputation.reviewersCount)) * 100)}%` }"></i></span><small>{{ sellerProfile.reputation.distribution[String(star)] ?? 0 }}</small></div></div>
        </div>
        <div class="profile-tabs"><button class="profile-tab" :class="{ active: sellerTab === 'ads' }" type="button" @click="sellerTab = 'ads'">Объявления <span>{{ sellerProfile?.counts.activeAds ?? 1 }}</span></button><button class="profile-tab" :class="{ active: sellerTab === 'reviews' }" type="button" @click="sellerTab = 'reviews'; loadSellerReviews()">Отзывы <span>{{ sellerProfile?.counts.reviews ?? 0 }}</span></button></div>
        <template v-if="sellerTab === 'ads'">
          <div class="listing-grid"><article class="listing-card" @click="openListing(currentListing.id)"><div class="listing-visual tint-lavender"><span class="visual-symbol" aria-hidden="true">{{ currentListing.emoji }}</span><span class="visual-note">реальные фотографии · фото товара</span></div><div class="listing-copy"><h4>{{ currentListing.title }}</h4><strong>{{ formatMoney(currentListing.price) }}</strong></div></article></div>
        </template>
        <template v-else>
          <div class="review-filters"><button class="filter-chip" :class="{ active: sellerRatingFilter === null }" type="button" @click="loadSellerReviews(null)">Все</button><button v-for="star in [5, 4, 3, 2, 1]" :key="star" class="filter-chip" :class="{ active: sellerRatingFilter === star }" type="button" @click="loadSellerReviews(star)">{{ star }} ★</button></div>
          <div v-if="!sellerReviews.length" class="empty-state compact"><h3>Пока нет опубликованных отзывов</h3><p>Они появятся после проверки взаимодействия участников.</p></div>
          <article v-for="review in sellerReviews" :key="review.id" class="seller-review-card">
            <div class="review-card-head"><strong>{{ review.author.displayName }}</strong><span class="stars">{{ '★'.repeat(review.rating) }}<span class="stars-muted">{{ '★'.repeat(5 - review.rating) }}</span></span><time>{{ review.createdAt.slice(0, 10) }}</time></div>
            <template v-if="reviewEditId === review.id">
              <div class="review-edit-controls"><div class="review-star-picker"><button v-for="star in [1, 2, 3, 4, 5]" :key="star" type="button" :class="{ selected: star <= reviewEditRating }" @click="reviewEditRating = star">★</button></div><select v-model="reviewEditOutcome" aria-label="Результат взаимодействия"><option value="bought">Покупка состоялась</option><option value="no_agreement">Не договорились</option><option value="declined">Сделка не состоялась</option></select></div>
              <textarea v-model="reviewEditText" class="review-textarea" minlength="20" maxlength="2000" aria-label="Текст отзыва"></textarea>
              <div class="review-action-row"><button class="primary-button small-button" type="button" @click="saveReviewEdit(review)">Сохранить</button><button class="text-button" type="button" @click="reviewEditId = null">Отмена</button></div>
            </template>
            <template v-else>
              <p>{{ review.text }}</p>
              <small class="review-outcome">{{ review.outcome === 'bought' ? 'Покупка состоялась' : review.outcome === 'declined' ? 'Сделка не состоялась' : 'Не договорились' }}</small>
            </template>
            <div v-if="review.reply" class="seller-reply"><strong>Ответ продавца</strong><p>{{ review.reply.text }}</p></div>
            <div class="review-action-row"><button class="text-button" type="button" @click="beginReviewEdit(review)">Изменить</button><button class="text-button danger-text" type="button" @click="hideReview(review)">Скрыть</button><button class="text-button" type="button" @click="reviewReportId = reviewReportId === review.id ? null : review.id">Пожаловаться</button><button class="text-button" type="button" @click="reviewReplyId = reviewReplyId === review.id ? null : review.id">Ответить</button></div>
            <div v-if="reviewReplyId === review.id" class="review-inline-form"><textarea v-model="reviewReplyText" class="review-textarea" maxlength="2000" placeholder="Ответ продавца" aria-label="Ответ продавца"></textarea><button class="secondary-button small-button" type="button" @click="saveReviewReply(review)">Сохранить ответ</button></div>
            <div v-if="reviewReportId === review.id" class="review-inline-form"><select v-model="reviewReportReason" aria-label="Причина жалобы"><option value="spam">Спам</option><option value="fraud">Недостоверная информация</option><option value="abuse">Оскорбления</option><option value="personal_data">Персональные данные</option><option value="other">Другое</option></select><textarea v-model="reviewReportDetails" class="review-textarea" maxlength="1000" placeholder="Комментарий к жалобе" aria-label="Комментарий к жалобе"></textarea><button class="secondary-button small-button" type="button" @click="submitReviewReport(review)">Отправить жалобу</button></div>
          </article>
          <button v-if="sellerReviewsHasMore" class="secondary-button load-more-button" type="button" @click="loadMoreSellerReviews">Показать ещё</button>
        </template>
      </section>

      <!-- 7. Избранное -->
      <section v-else-if="screen === 'favorites'" class="screen" aria-labelledby="favorites-heading"><div class="screen-heading"><p class="eyebrow">СОХРАНЁННОЕ</p><h2 id="favorites-heading">То, к чему хочется вернуться</h2><p class="subhead">Список не пропадёт при временном offline.</p></div><div v-if="!favoriteListings.length" class="empty-state"><div class="empty-illustration heart-empty" aria-hidden="true">♡</div><h3>Пока пусто</h3><p>Сохраняйте объявления, чтобы сравнить их позже.</p><button class="primary-button" type="button" @click="navigateFromNav('feed')">Открыть ленту</button></div><div v-else class="listing-grid"><article v-for="listing in favoriteListings" :key="listing.id" class="listing-card" @click="openListing(listing.id)"><div class="listing-visual" :class="`tint-${listing.tint}`"><span class="visual-symbol" aria-hidden="true">{{ listing.emoji }}</span><button class="favorite-button" type="button" aria-label="Убрать из избранного" @click.stop="toggleFavorite(listing)">♥</button></div><div class="listing-copy"><h4>{{ listing.title }}</h4><strong>{{ formatMoney(listing.price) }}</strong></div></article></div></section>

      <!-- 8. Профиль -->
      <section v-else-if="screen === 'profile'" class="screen" aria-labelledby="profile-heading"><div class="profile-card"><span class="profile-avatar seller-avatar">◌</span><div><p class="eyebrow">TELEGRAM MINI APP</p><h2 id="profile-heading">Sandbox demo</h2><p class="subhead">Сессия защищена Telegram-подписью; тестовые данные могут очищаться.</p></div></div><div class="info-note"><span class="quiet-icon">i</span><p><strong>В этой версии работают каталог, избранное, публикация с фото, личные сообщения и входящие уведомления.</strong><br />Отзывы, платежи, продвижение и модерация намеренно не включены.</p></div><button class="quiet-card wide-quiet" type="button" @click="navigateFromNav('feed')"><span class="quiet-icon">⌂</span><span><strong>Вернуться в каталог</strong><small>Открыть тестовые объявления</small></span><span>›</span></button></section>

      <!-- 9. Настройки -->
      <section v-else-if="screen === 'settings'" class="screen" aria-labelledby="settings-heading"><div class="screen-heading"><p class="eyebrow">ПРОФИЛЬ</p><h2 id="settings-heading">Настройки</h2><p class="subhead">Выберите удобный способ пользоваться Noname.</p></div><div class="settings-group"><p class="group-label">ВИД</p><div class="settings-row settings-info-row" role="status"><span><strong>Тема Telegram</strong><small>Приложение автоматически следует системной теме Telegram.</small></span><span class="theme-status">{{ themeLabel }}</span></div></div><div class="settings-group"><p class="group-label">УВЕДОМЛЕНИЯ</p><button class="settings-row" type="button" @click="go('notifications')"><span><strong>Центр уведомлений</strong><small>В приложении и через Telegram</small></span><span>›</span></button><button class="settings-row" type="button" @click="announce('Настройки сохранены')"><span><strong>Сообщения о новых ответах</strong><small>Включены</small></span><span class="toggle on"><i></i></span></button></div><div class="settings-group"><p class="group-label">ПРЕДПРОСМОТР</p><button class="settings-row" type="button" @click="toggleNetwork"><span><strong>Симулировать отсутствие сети</strong><small>{{ online ? 'Сейчас подключено' : 'Сейчас offline' }}</small></span><span class="toggle" :class="{ on: !online }"><i></i></span></button></div></section>

      <!-- 10. Уведомления -->
      <section v-else-if="screen === 'notifications'" class="screen" aria-labelledby="notifications-heading">
        <div class="screen-heading">
          <p class="eyebrow">ЦЕНТР УВЕДОМЛЕНИЙ</p>
          <h2 id="notifications-heading">Личные сообщения</h2>
          <p class="subhead">Текст сообщений не уходит в push-уведомления: он доступен только в вашем диалоге.</p>
        </div>
        <div v-if="telegramAdapter.isTelegram && conversations.length" class="notification-permission">
          <strong>Уведомления в Telegram</strong>
          <p>Запрос появится только после вашего нажатия. Можно продолжать пользоваться приложением без него.</p>
          <button class="secondary-button small-button" type="button" @click="requestTelegramWriteAccess">Разрешить</button>
        </div>
        <div v-if="inboxItems.length" class="notification-list">
          <button
            v-for="item in inboxItems"
            :key="item.id"
            class="notification-item"
            :class="{ unread: !item.readAt }"
            type="button"
            @click="openInboxItem(item)"
          >
            <span class="notification-icon mint-icon">↗</span>
            <span><strong>Новое сообщение</strong><small>{{ item.listingTitle }} · {{ formatInboxTime(item.createdAt) }}</small></span>
            <span v-if="!item.readAt" class="status-dot status-dot-accent"></span>
          </button>
        </div>
        <div v-else class="empty-state">
          <div class="empty-illustration" aria-hidden="true">⌁</div>
          <h3>Новых сообщений нет</h3>
          <p>Когда вам напишут по объявлению, уведомление появится здесь.</p>
        </div>
      </section>

      <!-- 11. Мои объявления -->
      <section v-else-if="screen === 'my-ads'" class="screen" aria-labelledby="my-ads-heading"><div class="screen-heading inline-heading"><div><p class="eyebrow">ПРОДАЖА</p><h2 id="my-ads-heading">Мои объявления</h2></div><button class="primary-button small-button" type="button" @click="openCreate">＋ Новое</button></div><div class="ad-status-tabs"><button class="active" type="button">Все <span>3</span></button><button type="button">Активные <span>1</span></button><button type="button">Черновики <span>1</span></button></div><div v-if="api.submitted" class="my-ad-row review-row"><div class="mini-visual tint-lavender"><span>⌂</span></div><div><strong>{{ draft.title || 'Кресло, которое ждёт новый дом' }}</strong><small>На проверке · отправлено сегодня</small></div><span class="status-pill status-review">На проверке</span></div><div class="my-ad-row"><div class="mini-visual tint-mint"><span>✦</span></div><div><strong>Настольная лампа</strong><small>Активно · 3 просмотра сегодня</small></div><span class="status-pill status-active">Активно</span></div><div class="my-ad-row"><div class="mini-visual tint-peach"><span>＋</span></div><div><strong>Пустой черновик</strong><small>Не заполнено · сохранено вчера</small></div><button class="text-button" type="button" @click="openCreate">Продолжить</button></div><div class="info-note"><span class="quiet-icon">i</span><p>При отклонении здесь появится точный список замечаний и кнопка «Исправить». Старая approved-версия остаётся видимой покупателям.</p></div></section>

      <!-- 12. Создание шаг 1 -->
      <section v-else-if="screen === 'create-1'" class="screen create-screen" aria-labelledby="create-heading"><div class="wizard-head"><p class="eyebrow">ШАГ 1 ИЗ 3</p><h2 id="create-heading">Расскажите о вещи</h2><div class="stepper"><span class="active"></span><span></span><span></span></div></div><label class="form-field"><span>Название</span><input v-model="draft.title" type="text" maxlength="120" placeholder="Например, кресло для чтения" @blur="updateDraft({ title: draft.title })" /><small>{{ draft.title.length }}/120</small></label><label class="form-field"><span>Цена</span><input v-model="draft.price" type="number" inputmode="numeric" min="0" placeholder="0" @blur="updateDraft({ price: draft.price })" /><small class="money-preview">{{ draft.price ? formatMoney(Number(draft.price)) : 'Бесплатно' }}</small></label><div class="form-field"><span>Категория</span><div class="choice-grid"><label v-for="category in categories.slice(1)" :key="category" class="compact-choice"><input v-model="draft.category" class="choice-input" type="radio" name="draft-category" :value="category" @change="updateDraft({ category: draft.category })" /><span>{{ category }}</span></label></div></div><div class="wizard-actions"><button class="secondary-button" type="button" @click="back">Назад</button><button class="primary-button" type="button" @click="continueCreate('create-2')">Продолжить</button></div></section>

      <!-- 13. Создание шаг 2 -->
      <section v-else-if="screen === 'create-2'" class="screen create-screen" aria-labelledby="create-2-heading"><div class="wizard-head"><p class="eyebrow">ШАГ 2 ИЗ 3</p><h2 id="create-2-heading">Добавьте контекст</h2><div class="stepper"><span class="complete">✓</span><span class="active"></span><span></span></div></div><div class="upload-grid"><label class="upload-slot"><input class="sr-only" type="file" accept="image/jpeg,image/png,image/webp" multiple aria-label="Фото объявления" @change="selectListingPhotos" /><span>＋</span><small>Добавить фото</small></label><div class="upload-slot upload-summary"><span>{{ selectedFiles.length || '＋' }}</span><small>{{ selectedFiles.length ? 'фото выбрано' : 'JPEG, PNG или WebP' }}</small></div><div class="upload-slot upload-summary"><span>12</span><small>максимум фото</small></div></div><p v-if="selectedFiles.length" class="field-hint" role="status">Выбрано фото: {{ selectedFiles.length }}</p><p class="field-hint">JPEG, PNG или WebP. До 12 фото; сервер проверит содержимое и размер перед публикацией.</p><div class="form-field"><span>Состояние</span><div class="choice-grid two-columns"><label v-for="condition in ['Новое', 'Отличное состояние', 'Хорошее состояние', 'Есть следы использования']" :key="condition" class="compact-choice"><input v-model="draft.condition" class="choice-input" type="radio" name="condition" :value="condition" @change="updateDraft({ condition: draft.condition })" /><span>{{ condition }}</span></label></div></div><div class="form-field">
  <span>Регион</span>
  <select v-model="draft.regionCode" aria-label="Регион объявления" @change="onDraftRegionChange">
    <option value="" disabled>Выберите регион</option>
    <option v-for="region in geographyRegions" :key="region.code" :value="region.code">{{ region.name }}</option>
  </select>
</div>
<div class="form-field">
  <span>Населённый пункт</span>
  <input v-model="draft.city" type="search" inputmode="search" maxlength="100" placeholder="Начните вводить населённый пункт" aria-label="Населённый пункт объявления" :disabled="!draft.regionCode" @input="onDraftCityInput" />
  <small v-if="draft.regionCode">Введите не менее двух букв и выберите вариант из справочника</small>
  <small v-else>Сначала выберите регион</small>
</div>
<div v-if="draftCitySuggestions.length" class="city-suggestions" role="listbox" aria-label="Подходящие населённые пункты для объявления">
  <button v-for="city in draftCitySuggestions" :key="city.id" type="button" role="option" @click="chooseDraftCity(city)">{{ city.name }} · {{ city.region.name }}</button>
</div>
<p v-if="draft.city && !draft.cityId" class="field-hint" role="status">Выберите населённый пункт из подсказки, чтобы продолжить.</p>
<p class="field-hint">Справочник: <a href="https://www.geonames.org/" target="_blank" rel="noreferrer">GeoNames</a> · CC BY 4.0.</p><div class="wizard-actions"><button class="secondary-button" type="button" @click="go('create-1')">Назад</button><button class="primary-button" type="button" @click="continueCreate('create-3')">Продолжить</button></div></section>

      <!-- 14. Создание шаг 3 -->
      <section v-else-if="screen === 'create-3'" class="screen create-screen" aria-labelledby="create-3-heading"><div class="wizard-head"><p class="eyebrow">ШАГ 3 ИЗ 3</p><h2 id="create-3-heading">Последние детали</h2><div class="stepper"><span class="complete">✓</span><span class="complete">✓</span><span class="active"></span></div></div><label class="form-field"><span>Описание</span><textarea v-model="draft.description" maxlength="1000" rows="5" placeholder="Что важно знать покупателю?" @blur="updateDraft({ description: draft.description })"></textarea><small>{{ draft.description.length }}/1000</small></label><div class="form-field"><span>Обсуждение</span><label class="toggle-row"><input v-model="draft.commentsEnabled" type="checkbox" @change="updateDraft({ commentsEnabled: draft.commentsEnabled })" /><span>Разрешить комментарии под объявлением</span></label></div><div class="form-field"><span>Когда можно забрать</span><div class="date-time-row"><label class="date-field"><span class="sr-only">Дата</span><input class="date-native" type="date" :value="draft.date" aria-label="Дата" @change="updateDraft({ date: ($event.target as HTMLInputElement).value })" /><span class="date-field-value">{{ formatDate(draft.date) }}</span></label><label class="time-field"><span class="sr-only">Время</span><input type="text" inputmode="numeric" maxlength="5" placeholder="18:00" :value="draft.time" aria-label="Время" @input="draft.time = ($event.target as HTMLInputElement).value" @blur="updateTime(draft.time)" @keyup.enter="updateTime(draft.time)" /></label></div></div><div class="preview-summary"><div class="summary-visual tint-lavender" aria-hidden="true">{{ draft.category ? draft.category[0] : 'N' }}</div><div><p class="eyebrow">ПРЕДПРОСМОТР</p><strong>{{ draft.title || 'Название объявления' }}</strong><span>{{ draft.price ? formatMoney(Number(draft.price)) : 'Бесплатно' }} · {{ draft.city }}</span></div></div><div class="free-note"><span class="quiet-icon">✓</span><p><strong>Размещение бесплатно</strong><br />После отправки объявление попадёт на проверку. Неполные поля не сотрутся при ошибке сети.</p></div><div class="wizard-actions"><button class="secondary-button" type="button" @click="continueCreate('create-2')">Назад</button><button class="primary-button" type="button" @click="submitDraft">Отправить на проверку</button></div></section>

      <!-- 15. Чаты -->
      <section v-else-if="screen === 'chats'" class="screen" aria-labelledby="chats-heading">
        <div class="screen-heading inline-heading">
          <div>
            <p class="eyebrow">ЛИЧНЫЕ СООБЩЕНИЯ</p>
            <h2 id="chats-heading">Ваши диалоги</h2>
            <p class="subhead">Фото и сообщения видят только участники.</p>
          </div>
          <button class="text-button inbox-link" type="button" @click="go('notifications')">
            Уведомления<span v-if="inboxUnread"> · {{ inboxUnread }}</span>
          </button>
        </div>
        <div class="chat-list">
          <button v-for="conversation in conversations" :key="conversation.id" class="chat-row" type="button" @click="openChat(conversation.id)">
            <span class="seller-avatar" :class="{ 'support-avatar': conversation.isSupport }">{{ conversation.isSupport ? "🗨️" : conversation.name[0] }}</span>
            <span class="chat-row-copy"><strong>{{ conversation.name }}</strong><small>{{ conversation.listing }}</small><p>{{ conversation.preview }}</p></span>
            <span class="chat-row-meta"><small>{{ conversation.time }}</small><b v-if="conversation.unread">{{ conversation.unread }}</b></span>
          </button>
        </div>
        <div v-if="!conversations.length" class="empty-state">
          <div class="empty-illustration" aria-hidden="true">🗨️</div><h3>Чатов пока нет</h3><p>Напишите продавцу из карточки объявления.</p>
        </div>
      </section>

      <!-- 16. Диалог -->
      <section v-else-if="screen === 'chat' && activeChat" class="screen chat-screen" aria-labelledby="chat-heading">
        <div class="chat-context">
          <div class="mini-visual tint-lavender"><span>{{ activeChat.isSupport ? "🗨️" : "⌂" }}</span></div>
          <span><small>{{ activeChat.isSupport ? "Поддержка" : "Объявление" }}</small><strong id="chat-heading">{{ activeChat.listing }}</strong></span>
          <div class="chat-context-actions">
            <button v-if="activeChat.canMarkSold" class="chat-deal-action" type="button" @click="markConversationSold"><AppIcon name="check" :size="15" /> Продано</button>
            <span v-else-if="activeChat.dealStatus === 'sold'" class="chat-deal-status"><AppIcon name="check" :size="15" /> Продано</span>
            <button v-if="!activeChat.isSupport" class="text-button chat-safety-action" type="button" @click="toggleConversationBlock">{{ activeChatBlocked ? "Разблокировать" : "Заблокировать" }}</button>
          </div>
        </div>
        <div class="privacy-note"><span>{{ activeChat.isSupport ? "🗨️" : "⌁" }}</span><span>{{ activeChat.isSupport ? "Опишите вопрос или баг. Сообщение сохранится в очереди поддержки." : "Личные сообщения защищены. Не отправляйте коды и данные оплаты." }}</span></div>
        <div v-if="activeChat.canRate" class="rating-actions"><span>Как прошла сделка?</span><button v-for="star in 5" :key="star" type="button" :class="{ selected: ratingChoice !== null && star <= ratingChoice }" :aria-label="`${star} из 5`" @click="ratingChoice = star">★</button><input v-model="ratingText" class="rating-text-input" type="text" maxlength="2000" placeholder="Короткий отзыв (минимум 20 символов)" aria-label="Текст отзыва" /><button class="rating-submit" type="button" :disabled="!ratingChoice || ratingText.trim().length < 20" @click="submitChatRating">Сохранить</button></div>
        <div class="messages" aria-live="polite">
          <div v-for="message in activeChat.messages" :key="message.id" class="message" :class="message.author === 'me' ? 'message-me' : 'message-other'">
            <div v-if="message.attachments?.length" class="message-attachments">
              <template v-for="attachment in message.attachments" :key="attachment.id">
                <img v-if="attachment.url" :src="attachment.url" class="message-photo" alt="Фото из личного сообщения" />
                <span v-else class="message-photo-unavailable">Фото временно недоступно</span>
              </template>
            </div>
            <p v-if="message.text">{{ message.text }}</p>
            <button v-if="message.author === 'other' && !activeChat.isSupport" class="message-report" type="button" @click="openMessageReport(message.id)">Пожаловаться</button>
            <time>{{ formatTime(message.time) }}<span v-if="message.author === 'me'" class="message-status">{{ message.status === "read" ? "Прочитано" : "Отправлено" }}</span></time>
          </div>
        </div>
        <div v-if="chatPhotoFiles.length" class="chat-photo-queue" role="status" aria-label="Фото для отправки">
          <span>{{ chatPhotoFiles.length }} фото выбрано</span>
          <ul>
            <li v-for="(file, index) in chatPhotoFiles" :key="`${file.name}-${file.lastModified}-${index}`">
              <img
                v-if="chatPhotoPreviews.get(file)"
                :src="chatPhotoPreviews.get(file)"
                class="chat-photo-preview"
                :alt="`Предпросмотр ${file.name}`"
              />
              <span>{{ file.name }}</span>
              <button type="button" :aria-label="`Убрать ${file.name}`" @click="removeChatPhoto(index)">×</button>
            </li>
          </ul>
          <small class="chat-photo-privacy">Доступно только участникам диалога</small>
        </div>
        <p v-if="activeChatBlocked" class="chat-blocked-note" role="status">Вы заблокировали пользователя. Новые сообщения в этом диалоге не отправляются.</p>
        <div class="composer chat-composer">
          <label v-if="!activeChat.isSupport" class="chat-photo-trigger" title="Добавить фото">
            <input class="sr-only" type="file" accept="image/jpeg,image/png,image/webp" multiple aria-label="Фото сообщения" :disabled="chatSending || activeChatBlocked" @change="selectChatPhotos" />
            <span aria-hidden="true">＋</span><span class="sr-only">Добавить фото</span>
          </label>
          <input v-model="messageInput" type="text" :placeholder="activeChat.isSupport ? 'Опишите вопрос или баг' : 'Сообщение'" aria-label="Сообщение" :disabled="chatSending || activeChatBlocked || activeChat.dealStatus === 'sold'" @keyup.enter="sendMessage" />
          <button class="send-button" type="button" :aria-label="chatSending ? 'Отправляем сообщение' : 'Отправить сообщение'" :disabled="chatSending || activeChatBlocked || activeChat.dealStatus === 'sold' || (!messageInput.trim() && !chatPhotoFiles.length)" @click="sendMessage">{{ chatSending ? "…" : "↑" }}</button>
        </div>
      </section>

      <!-- 17. Продвижение / платежи -->
      <section v-else-if="screen === 'promotions'" class="screen" aria-labelledby="promotions-heading"><div class="screen-heading"><p class="eyebrow">ДЛЯ ПРОДАВЦА</p><h2 id="promotions-heading">Продвижение</h2><p class="subhead">Понятный эффект, оплата только после подтверждения сервера.</p></div><div class="service-card featured-service"><div class="service-top"><span class="service-icon">↑</span><span class="soft-pill">БЫСТРЫЙ ЭФФЕКТ</span></div><h3>Поднять объявление</h3><p>Вернём карточку в начало ленты один раз.</p><div class="service-bottom"><strong>15 XTR</strong><button class="primary-button small-button" type="button" @click="announce('Invoice откроется на E10')">Выбрать</button></div></div><div class="service-card"><div class="service-top"><span class="service-icon accent-icon">✦</span><span class="soft-pill">ВИДИМЕЕ</span></div><h3>Выделить объявление</h3><p>Аккуратный акцент на карточке на 7 дней.</p><div class="service-bottom"><strong>25 XTR</strong><button class="secondary-button small-button" type="button" @click="announce('Invoice откроется на E10')">Выбрать</button></div></div><div class="section-heading compact"><div><p class="eyebrow">ИСТОРИЯ</p><h3>Платежи в Stars</h3></div><button class="text-button" type="button" @click="announce('История актуальна')">Обновить</button></div><div class="payment-row"><span class="payment-icon success-icon">✓</span><span><strong>Поднять объявление</strong><small>16.09.2026 · order preview</small></span><b>−15 XTR</b></div><div class="payment-row"><span class="payment-icon pending-icon">…</span><span><strong>Проверка оплаты</strong><small>Повторный update не создаст дубль</small></span><b class="pending-text">На разборе</b></div><div class="support-note"><strong>Нужна помощь?</strong><p>Support доступен даже если бот не может прислать уведомление.</p><button class="text-button" type="button" @click="openSupportChat">Открыть поддержку →</button></div></section>

      <!-- 18. Модератор -->
      <section v-else-if="screen === 'moderator'" class="screen" aria-labelledby="moderator-heading"><div class="screen-heading"><p class="eyebrow">ROLE · MODERATOR</p><h2 id="moderator-heading">Очередь проверки</h2><p class="subhead">Действия ограничены Mini App. Credentials и админка недоступны.</p></div><div class="moderator-stats"><div><strong>{{ moderationQueue.length }}</strong><span>на проверке</span></div><div><strong>{{ moderationQueue.reduce((sum, item) => sum + item.reports.length, 0) }}</strong><span>жалобы</span></div><div><strong>—</strong><span>просрочено</span></div></div>
      <div v-if="moderationLoading" class="empty-state compact"><p>Загружаем очередь модерации…</p></div>
      <div v-else-if="!moderationQueue.length" class="empty-state compact"><h3>Очередь пуста</h3><p>Сейчас нет отзывов, ожидающих решения или жалоб.</p></div>
      <article v-for="item in moderationQueue" v-else :key="item.reviewId" class="moderation-card"><div class="moderation-copy"><span class="soft-pill">{{ item.status === 'pending' ? 'НА ПРОВЕРКЕ' : 'ЖАЛОБА' }}</span><h3>{{ item.ad.title }}</h3><p>{{ item.text }}</p><small>{{ item.author }} → {{ item.seller }} · {{ item.rating }} ★</small><p v-if="item.reports.length">Причина: {{ item.reports[0].reason }}. {{ item.reports[0].details }}</p><div class="moderation-actions"><button class="primary-button small-button" type="button" @click="void moderateReview(item, 'approve')">Опубликовать</button><button class="secondary-button small-button" type="button" @click="void moderateReview(item, 'reject')">Отклонить</button><button class="text-button" type="button" @click="void moderateReview(item, 'hide')">Скрыть</button></div></div></article>
      <div v-if="moderationQueue.length" class="moderator-boundary"><span class="quiet-icon">✓</span><p><strong>Все действия журналируются</strong><br />Публичные counters и тестовые объекты не используются.</p></div>
      </section>
      </template>
     </main>

    <nav class="bottom-nav" :class="{ 'sandbox-catalog-nav': sandboxCatalogPreview }" aria-label="Основная навигация">
      <template v-if="sandboxCatalogPreview">
        <button class="sandbox-feed-nav" type="button" @click="navigateFromNav('feed')"><span aria-hidden="true">⌂</span><small>Лента</small></button>
        <div class="sandbox-nav-status" role="status"><span aria-hidden="true">◉</span><small>Только просмотр</small></div>
      </template>
      <template v-else>
        <button type="button" :class="{ active: screen === 'feed' || screen === 'search' }" @click="navigateFromNav('feed')"><AppIcon name="home" /><small>Лента</small></button>
        <button type="button" :class="{ active: screen === 'favorites' }" @click="navigateFromNav('favorites')"><AppIcon name="heart" /><small>Избранное</small></button>
        <button type="button" :class="{ active: screen.startsWith('create-') }" @click="navigateFromNav('create-1')"><span class="sell-nav-icon"><AppIcon name="plus" :size="24" /></span><small>Продать</small></button>
        <button type="button" :class="{ active: screen === 'chats' || screen === 'chat' }" @click="navigateFromNav('chats')"><AppIcon name="message" /><small>Чаты</small><b v-if="conversations.some((item) => item.unread)" class="nav-badge">1</b></button>
        <button type="button" :class="{ active: ['profile', 'settings', 'notifications', 'my-ads', 'promotions', 'moderator'].includes(screen) }" @click="navigateFromNav('profile')"><AppIcon name="user" /><small>Профиль</small></button>
      </template>
    </nav>

    <div v-if="filterOpen" class="sheet-backdrop" @click.self="filterOpen = false"><section class="bottom-sheet" role="dialog" aria-modal="true" aria-labelledby="filter-heading"><div class="sheet-handle"></div><div class="sheet-head"><div><p class="eyebrow">КАТАЛОГ</p><h2 id="filter-heading">Фильтры</h2></div><button class="icon-button" type="button" aria-label="Закрыть фильтры" @click="filterOpen = false">×</button></div><div class="filter-fields"><label class="form-field"><span>Цена от</span><input v-model="filters.min" type="number" inputmode="numeric" placeholder="0" /></label><label class="form-field"><span>Цена до</span><input v-model="filters.max" type="number" inputmode="numeric" placeholder="без лимита" /></label></div><label class="switch-row"><span><strong>Только новые</strong><small>Без следов использования</small></span><input v-model="filters.onlyNew" type="checkbox" /><span class="toggle" :class="{ on: filters.onlyNew }"><i></i></span></label><div class="sheet-actions"><button class="secondary-button" type="button" @click="clearFilters">Сбросить</button><button class="primary-button" type="button" @click="applyFilters">Показать {{ filteredListings.length }}</button></div></section></div>
    <div v-if="reportSheetOpen" class="sheet-backdrop" @click.self="reportSheetOpen = false">
      <section class="bottom-sheet" role="dialog" aria-modal="true" aria-labelledby="message-report-heading">
        <div class="sheet-handle"></div>
        <div class="sheet-head">
          <div>
            <p class="eyebrow">ЛИЧНЫЕ СООБЩЕНИЯ</p>
            <h2 id="message-report-heading">Почему вы жалуетесь?</h2>
          </div>
          <button class="icon-button" type="button" aria-label="Закрыть жалобу" @click="reportSheetOpen = false">×</button>
        </div>
        <div class="danger-choice-list">
          <button type="button" @click="submitMessageReport('spam')">
            <span class="action-symbol">!</span><span><strong>Спам</strong><small>Навязчивая или массовая рассылка</small></span><span>›</span>
          </button>
          <button type="button" @click="submitMessageReport('fraud')">
            <span class="action-symbol">⌁</span><span><strong>Мошенничество</strong><small>Попытка обмана или небезопасная сделка</small></span><span>›</span>
          </button>
          <button type="button" @click="submitMessageReport('abuse')">
            <span class="action-symbol">⊘</span><span><strong>Оскорбления</strong><small>Угрозы, травля или грубость</small></span><span>›</span>
          </button>
          <button type="button" @click="submitMessageReport('other')">
            <span class="action-symbol">?</span><span><strong>Другая причина</strong><small>Сообщение нарушает правила иным образом</small></span><span>›</span>
          </button>
        </div>
      </section>
    </div>
    <div v-if="moderatorActionOpen" class="sheet-backdrop" @click.self="moderatorActionOpen = false"><section class="bottom-sheet" role="dialog" aria-modal="true" aria-labelledby="mod-action-heading"><div class="sheet-handle"></div><div class="sheet-head"><div><p class="eyebrow">ОБЪЕКТ · ЖАЛОБА</p><h2 id="mod-action-heading">Что сделать?</h2></div><button class="icon-button" type="button" aria-label="Закрыть" @click="moderatorActionOpen = false">×</button></div><div class="danger-choice-list"><button type="button" @click="moderatorActionOpen = false; announce('Объявление скрыто, доказательства сохранены')"><span class="action-symbol">⌁</span><span><strong>Скрыть объявление</strong><small>Контент станет недоступен публично</small></span><span>›</span></button><button type="button" @click="moderatorActionOpen = false; announce('Автор получит предупреждение')"><span class="action-symbol">!</span><span><strong>Отправить предупреждение</strong><small>Причина попадёт в журнал действий</small></span><span>›</span></button><button class="destructive-row" type="button" @click="moderatorActionOpen = false; announce('Бан требует причины и срока')"><span class="action-symbol">⊘</span><span><strong>Временный бан</strong><small>Срок и причина обязательны</small></span><span>›</span></button></div></section></div>
    <div v-if="toast" class="toast" role="status">{{ toast }}</div>
  </div>
</template>
